/**
 * The client-side video-slide player (spec 0020 Slice 3) — React-free, like the slider engine, so the
 * storefront's ONE global init script upgrades every SSR'd `[data-aqx-video]` with zero framework JS. It is
 * the whole reason a video slide keeps a top PageSpeed score: the SSR markup ships `preload="none"` +
 * `<source data-src>` (no bytes), and this engine only promotes `data-src`→`src` when a slide actually needs
 * to play. Nothing here runs on the server (the module only DEFINES functions; the storefront calls it once).
 *
 * Contract (the chrome is rendered by `_shared/videoControls.tsx`; the engine only flips state):
 *   [data-aqx-video][data-mode="autoplay"|"click"]      the slide wrapper
 *     ← the engine WRITES data-playing="true|false" and data-muted="true|false" here, and the chrome's
 *       CSS reads them to decide which icon of each pair is visible. The engine never rewrites a button's
 *       contents, so the icons can be real SVG instead of a text glyph painted by the visitor's font.
 *     [data-aqx-video-el]                                the <video> (sources carry data-src, not src)
 *     [data-aqx-video-play]  (optional)                  play/pause toggle
 *          [data-label-play] / [data-label-pause]        its two aria-labels, ALREADY TRANSLATED
 *     [data-aqx-video-sound] (optional)                  mute toggle
 *          [data-label-mute] / [data-label-unmute]       idem
 *     [data-aqx-video-fs]    (optional)                  fullscreen
 *     [data-aqx-video-timeline] > [data-aqx-video-progress]  (optional) scrub bar
 *
 * The labels ride in the markup because this file is vanilla JS shipped to every storefront and has no
 * dictionary; the React section that prints the button already knows the reader's language.
 */
/**
 * Promote a lazy video's sources to real `src` and load — idempotent, but keyed on the CONTENT, not on a
 * bare boolean.
 *
 * The flag used to be `dataset.promoted === '1'`, set once and never cleared. React keeps the `<video>`
 * DOM node across re-renders (the key sits on the wrapping `<li>`/slide, not on the element), so swapping
 * the media only rewrote the child `<source data-src>` — the already-promoted `<video>` was skipped, never
 * re-`load()`ed, and kept playing the OLD clip. From the seller's chair the new video "never loads", so
 * they re-upload again and again. Keying on the first data-src makes a changed source promote again.
 */
export function promoteVideo(video) {
    const sources = Array.from(video.querySelectorAll('source[data-src]'));
    // Two shapes are supported: `<video><source data-src>` (the slider/hero slides) and a bare
    // `<video data-src>` (sections that render a single URL with no <source> children).
    const own = video.dataset.src ?? '';
    const key = own || sources.map((s) => s.dataset.src ?? '').join('|');
    // No lazy sources at all (already a plain `src`, or an empty slide) → nothing to promote.
    if (!key)
        return;
    if (video.dataset.promotedSrc === key)
        return;
    video.dataset.promotedSrc = key;
    if (own)
        video.src = own;
    // Promote ONLY the rendition that fits. A browser picks the first source it can play, not the smallest
    // that fits, so leaving them all with a real `src` would hand a phone the desktop file.
    // CODEC FIRST, WIDTH SECOND (2026-09-22). The ladder now carries a VP9/WebM twin beside each H.264
    // rung, and `pickRendition` only knows about pixels — handed the whole list it would cheerfully give a
    // browser that cannot decode WebM a WebM. Normally a `<video>` with several `<source>` children falls
    // back on its own, but three lines below we give a real `src` to ONE of them and strip the rest, so
    // that native safety net is disarmed HERE, by us. Filtering first is what keeps it honest.
    const usable = playableSources(sources);
    const chosen = pickRendition(video, usable);
    sources.forEach((s) => {
        if (s === chosen && s.dataset.src)
            s.src = s.dataset.src;
        else
            s.removeAttribute('src');
    });
    video.load();
    // `usable`, not `sources`: trading UP into a rung this browser cannot decode would undo the filter.
    if (chosen)
        verifyRendition(video, usable, chosen);
    recoverFromTrouble(video);
}
/**
 * Step DOWN a rung when the rendition we picked will not play — because the browser cannot decode it, or
 * because the line cannot carry it in time.
 *
 * THE MIRROR OF `verifyRendition`, AND THE REASON THIS FILE NEEDED BOTH. That one only ever trades UP, on
 * the assumption that whatever arrives, arrives. Both halves of the founder's 2026-09-16 report are the
 * other case:
 *
 *  · "it does not even load, it is blocked" — her clip was HEVC 10-bit. Safari decodes it, Firefox never
 *    does, Chrome only where the platform lends a hardware decoder. A `<video>` with several `<source>`
 *    children normally falls back on its own, but `promoteVideo` gives a real `src` to ONE of them and
 *    strips the rest (otherwise a browser takes the first it can play, not the smallest that fits) — so
 *    that native safety net is disarmed here, by us, and an undecodable pick left the band empty for good.
 *  · "it is too slow on desktop, it freezes" — 4.18 MB for a four-second hero, about a minute on her line.
 *    Nothing was broken; the file was simply never going to arrive while anyone was still looking.
 *
 * A step down is worth far more than either failure: a decorative clip at 640 px that PLAYS beats a 1080p
 * one that does not. The walk is one rung at a time and stops at the smallest, so it can never loop, and
 * it marks the element so `verifyRendition` does not immediately trade back up into the same wall.
 *
 * This is also the only half of the 2026-09-16 fix that repairs clips ALREADY on a storefront: the encoder
 * changes only apply to new uploads, while this runs against whatever is on the shelf today.
 */
function recoverFromTrouble(video) {
    // Bound to the ELEMENT, not to a promotion: React keeps the node across re-renders and `promoteVideo`
    // runs again on every media change, so a per-call binding would stack listeners for the tab's lifetime.
    if (video.dataset.rungRecovery === '1')
        return;
    video.dataset.rungRecovery = '1';
    let stallTimer;
    const clearStall = () => {
        if (stallTimer !== undefined)
            clearTimeout(stallTimer);
        stallTimer = undefined;
    };
    // Descents have two very different causes, and only ONE of them may walk the whole ladder.
    //   · a file that cannot be decoded — keep stepping until something plays, or the band stays empty;
    //   · a file that is slow to arrive — step ONCE. Beyond that the visitor is no longer being rescued,
    //     they are being handed a 360 px file for a 1500 px hero, which is exactly the 2026-09-17 report
    //     ("on dirait un film de 240px"). The poster covers the wait; a blurry upscale covers nothing.
    let bandwidthDescents = 0;
    // Set the first time the clip actually moves on screen. After that, a short loop is never traded away
    // for bandwidth: it stutters for a few seconds, finishes downloading, and then loops perfectly at the
    // quality the seller chose — whereas a descent costs that quality for the whole visit. Measured on the
    // founder's hero: 928 kB at ~100 kB/s starts at 19 s, rebuffers twice, and is whole by ~25 s. A LONG
    // clip is the other case — it would stutter all the way through — so it keeps its one descent.
    let hasPlayed = false;
    video.addEventListener('playing', () => {
        hasPlayed = true;
    });
    const stepDown = (reason) => {
        clearStall();
        if (reason === 'slow' && bandwidthDescents >= MAX_BANDWIDTH_DESCENTS)
            return;
        if (reason === 'slow' && hasPlayed && isShortLoop(video))
            return;
        // Read the ladder from the LIVE DOM every time. Capturing it would go stale the moment the seller
        // swaps the clip in the builder — the element survives, its sources do not.
        const rungs = playableSources(Array.from(video.querySelectorAll('source[data-src]')))
            .map((s) => ({ s, w: Number(s.dataset.width) || 0 }))
            .sort((a, b) => a.w - b.w);
        const playing = rungs.findIndex((r) => r.s.hasAttribute('src'));
        // Already on the smallest rung (or we lost track of which one is live): there is nothing left to try,
        // and retrying the same file is how a recovery turns into a loop.
        if (playing <= 0)
            return;
        const next = rungs[playing - 1];
        if (!next.s.dataset.src)
            return;
        // Remember it, so `verifyRendition` cannot answer "that file is narrower than the box" by climbing
        // straight back to the rung we just failed on.
        video.dataset.renditionSteppedDown = '1';
        if (reason === 'slow')
            bandwidthDescents += 1;
        rungs.forEach(({ s }) => {
            if (s === next.s)
                s.src = s.dataset.src;
            else
                s.removeAttribute('src');
        });
        const wasPlaying = !video.paused;
        video.load();
        if (wasPlaying)
            void video.play().catch(() => undefined);
    };
    // A decode or network failure on the file itself. Media errors do not bubble, so BOTH the element and
    // each `<source>` have to be listened to: the element fires when the chosen source fails to decode, the
    // source fires when the browser rejects it outright.
    //
    // THE `src` CHECK IS THE WHOLE POINT (2026-09-17). We strip `src` from every rung we did not choose —
    // and a `<source>` with no `src` is, to the resource selection algorithm, a source that failed: the
    // browser fires `error` on each of them at every `load()`. Measured on the founder's own hero in real
    // Chrome, on a healthy line: promoting the 1920 rung fired `error` from the emptied 640 source 36 ms
    // later, that descent emptied it again, and the clip landed on 640x360 inside a box painted 1512 px —
    // the ladder walked to the bottom without anything being wrong. So an error only counts when it comes
    // from the rung that is actually LIVE.
    const onError = (e) => {
        const target = e.target;
        if (target instanceof HTMLSourceElement && !target.hasAttribute('src'))
            return;
        stepDown('broken');
    };
    video.addEventListener('error', onError);
    video
        .querySelectorAll('source')
        .forEach((s) => s.addEventListener('error', onError));
    // Buffering. `waiting` fires the moment playback has nothing to show, `stalled` when the network has
    // gone quiet — neither is a failure on its own (every clip "waits" while its first bytes arrive), so the
    // grace period is what separates a stream that went quiet from one that is merely taking its time.
    //
    // `receiving` IS WHAT MAKES THAT DISTINCTION POSSIBLE (measured 2026-09-17, real Chrome). A browser
    // fires NO `progress` at all until the first byte of the file lands — on this page, `waiting` came at
    // 216 ms and the first `progress` 33 s later, so a countdown armed by `waiting` alone was timing the
    // server's answer, not the download, and condemned the rung before a single byte existed. Until data
    // starts arriving the only judge is [SLOW_START_BUDGET_MS]; the stall countdown only applies AFTER.
    let receiving = false;
    const armStall = () => {
        if (!receiving)
            return;
        if (stallTimer !== undefined)
            return;
        stallTimer = setTimeout(() => stepDown('slow'), STALL_GRACE_MS);
    };
    video.addEventListener('waiting', armStall);
    video.addEventListener('stalled', armStall);
    ['playing', 'canplaythrough', 'timeupdate'].forEach((ev) => video.addEventListener(ev, clearStall));
    // BYTES ARRIVING IS NOT A STALL. `progress` fires every few hundred ms while the file downloads, so a
    // big rung on a modest line looks exactly like a stall to a bare timer: measured on the founder's line
    // (~100 kB/s), her 1080p hero takes 9 s to land, and a 4 s countdown that ignores progress condemns it
    // before it ever had a chance. Each packet therefore restarts the countdown; the descent is left for a
    // stream that has genuinely gone quiet.
    ['progress', 'loadedmetadata', 'loadeddata'].forEach((ev) => video.addEventListener(ev, () => {
        receiving = true;
        if (stallTimer === undefined)
            return;
        clearStall();
        armStall();
    }));
    // A fresh `load()` starts a new file: nothing has arrived for it yet.
    video.addEventListener('emptied', () => {
        receiving = false;
        clearStall();
    });
    // The backstop for a line so slow the file is not coming in any useful time. It is deliberately long —
    // the poster is on screen throughout, and one rung down is all it ever buys (see `bandwidthDescents`).
    video.addEventListener('loadstart', () => {
        const started = setTimeout(() => {
            // Paused = nobody is waiting on it (a click-to-play clip that was promoted but never played).
            if (video.paused || video.readyState >= HAVE_FUTURE_DATA)
                return;
            stepDown('slow');
        }, SLOW_START_BUDGET_MS);
        ['playing', 'canplaythrough', 'timeupdate', 'emptied'].forEach((ev) => video.addEventListener(ev, () => clearTimeout(started), { once: true }));
    });
}
/**
 * A clip short enough that waiting out the buffering beats downgrading it — every hero, band and slide
 * loop we ship. An unknown duration counts as short: the seller asked for this rung, and the poster is
 * what shows meanwhile.
 */
function isShortLoop(video) {
    const seconds = video.duration;
    return !Number.isFinite(seconds) || seconds <= LOOP_SECONDS;
}
/** Past this, a clip is something a visitor WATCHES, and stuttering through it is worse than one rung. */
const LOOP_SECONDS = 30;
/** `readyState` at which the clip has enough buffered to keep playing — i.e. the wait is over. */
const HAVE_FUTURE_DATA = 3;
/**
 * How many rungs a SLOW line may cost the picture. One.
 *
 * The 2026-09-16 descent had no limit, and on a desktop hero that is the difference between a 1280 rung
 * (good enough that nobody writes in) and the 640 one, upscaled 2.4x across the whole viewport.
 */
const MAX_BANDWIDTH_DESCENTS = 1;
/**
 * How long a clip may take to reach playback before we accept that this rung is too heavy for this
 * visitor — counted from `loadstart`, not from the first hiccup.
 *
 * 20 s is set ABOVE the real cases we can measure (9 s for a 928 kB hero at ~100 kB/s) and below the
 * hopeless ones (the same file on a 30 kB/s line needs half a minute). Anything tighter starts punishing
 * lines that were about to succeed.
 */
const SLOW_START_BUDGET_MS = 20_000;
/**
 * How long a clip may sit there with NOTHING ARRIVING before we decide its rung is too heavy for this
 * visitor. Every `progress` event restarts it (see the listener above), so this measures a quiet stream,
 * not a slow one — a download that is merely taking its time is answered by [SLOW_START_BUDGET_MS].
 */
const STALL_GRACE_MS = 4000;
/**
 * Check, ONCE the file has told us its real size, that the rendition we picked is as wide as it claimed —
 * and trade up when it was not.
 *
 * WHY A PICKER NEEDS THIS AT ALL. The widths come from the page document, written at upload time, and until
 * 2026-09-16 they were guesses: the editor wrote the dimensions the SELLER'S browser had measured on the
 * file before sending it, and a flat `640` for the phone rendition. Both are wrong the moment the server
 * re-encodes — measured on a live storefront clip, the document said 1080 for a stored file of 720, and 640
 * for a rendition that is really 360 wide (ffmpeg scales the LONG edge, so a portrait clip comes out
 * 360x640). So this picker was handing a 360 px file to a box asking for up to 640, and the visitor watched
 * a blurry upscale of a 72 kbit/s copy while the seller's editor — which previews their own local file —
 * showed the clip pristine. That is the founder's report: "good in the builder, degraded in the store".
 *
 * The upload path now stores the real numbers, but EVERY CLIP UPLOADED BEFORE keeps the wrong ones in its
 * document, and no page re-saves itself. `videoWidth` is the ground truth the file itself provides, for
 * free, on `loadedmetadata`. So: if what arrived is materially narrower than the box needs and a bigger
 * rendition exists, promote that one instead. It costs a second request ONLY on a clip whose document lied
 * — a correct one never trades up — and it repairs those clips without a migration.
 *
 * `MIN_COVERAGE` leaves a deliberate margin: a rendition slightly under the needed width is invisible,
 * while re-fetching for it would double the bytes for nothing.
 */
function verifyRendition(video, sources, chosen) {
    if (sources.length < 2)
        return;
    // One trade-up per promotion, so a document whose widths are ALL wrong can never loop.
    if (video.dataset.renditionVerified === video.dataset.promotedSrc)
        return;
    const onMeta = () => {
        video.removeEventListener('loadedmetadata', onMeta);
        video.dataset.renditionVerified = video.dataset.promotedSrc ?? '1';
        // A rung we stepped DOWN to is narrow ON PURPOSE (see `recoverFromTrouble`). Trading up from it would
        // walk straight back into the file that could not decode or could not arrive, and the two helpers
        // would hand the clip back and forth for as long as the visitor stayed on the page.
        if (video.dataset.renditionSteppedDown === '1')
            return;
        const real = video.videoWidth;
        const needed = neededWidth(video);
        if (!real || !Number.isFinite(needed) || real >= needed * MIN_COVERAGE)
            return;
        // Take the smallest rendition that is WIDER than what just arrived — the document's widths are not
        // trustworthy here (that is the whole point), but their ORDER still is: the file behind a larger
        // claimed width is a larger encode of the same clip.
        const bigger = sources
            .filter((s) => s !== chosen && s.dataset.src)
            .map((s) => ({ s, w: Number(s.dataset.width) || 0 }))
            .filter((c) => c.w > (Number(chosen.dataset.width) || 0))
            .sort((a, b) => a.w - b.w)[0];
        if (!bigger)
            return;
        sources.forEach((s) => {
            if (s === bigger.s && s.dataset.src)
                s.src = s.dataset.src;
            else
                s.removeAttribute('src');
        });
        const wasPlaying = !video.paused;
        video.load();
        if (wasPlaying)
            void video.play().catch(() => undefined);
    };
    video.addEventListener('loadedmetadata', onMeta);
}
/**
 * How wide the file needs to be to cover the box it is painted in, at this screen's density.
 *
 * THE BOX'S WIDTH IS NOT THE ANSWER WHEN THE CLIP IS CROPPED (2026-09-18). Every section paints an
 * uploaded clip with `object-fit: cover`, and cover scales the source until it covers BOTH axes — so the
 * pixels actually spent across the width are `height x the clip's aspect ratio` whenever the clip is wider
 * than its tile. Measured in real Chromium on a multicolumn card (square tile, the section default, 16/9
 * clip):
 *
 * | tile        | box width x dpr (what we asked for) | cover width x dpr (what is painted) | rung taken |
 * |---|---|---|---|
 * | builder canvas, 300 px | 600  | 1067 | **640**  — upscaled 1.67x |
 * | storefront, 477 px     | 955  | 1697 | **1280** — upscaled 1.33x |
 *
 * That table IS the founder's report: she picks "Original quality", the builder pixelates, the storefront
 * looks fine. Nothing differs between the two but the width of the column — the builder's canvas is
 * narrower than a real viewport by the two rails, which drops the pick a whole rung, onto the 640 copy
 * encoded at CRF 28 for phones. Both surfaces were under-picking; only the builder was under-picking
 * badly enough to see.
 *
 * The aspect comes from the file itself once it has loaded, and before that from the rungs' own
 * `data-width`/`data-height` — which is why `videoSourceTags` emits the height. With neither (a clip
 * uploaded before renditions carried dimensions) this falls back to the box width, exactly as before, and
 * `verifyRendition` still repairs the pick on `loadedmetadata`.
 */
function neededWidth(video) {
    const dpr = typeof devicePixelRatio === 'number' && devicePixelRatio > 0 ? devicePixelRatio : 1;
    const rect = video.getBoundingClientRect();
    const css = rect.width || video.clientWidth;
    if (!(css > 0))
        return Infinity;
    const height = rect.height || video.clientHeight;
    const aspect = clipAspect(video);
    // `contain`, `fill` and the `<video>` default all paint the clip INSIDE the box: the box's own width is
    // then the honest answer, and inflating it would hand every letterboxed clip a rung it cannot use.
    const cropped = isCover(video) && aspect > 0 && height > 0;
    return (cropped ? Math.max(css, height * aspect) : css) * dpr;
}
/** Whether this clip is cropped to its tile rather than fitted inside it. */
function isCover(video) {
    if (typeof getComputedStyle !== 'function')
        return false;
    return getComputedStyle(video).objectFit === 'cover';
}
/**
 * The clip's own width/height ratio, or 0 when nothing on the page knows it yet.
 *
 * `videoWidth` is ground truth but only exists after `loadedmetadata` — i.e. after the file we are trying
 * to CHOOSE has already been downloaded. The rungs' declared dimensions are what make the first pick
 * right; they are all encodes of the same clip, so any rung carrying both numbers gives the same ratio.
 */
function clipAspect(video) {
    if (video.videoWidth > 0 && video.videoHeight > 0)
        return video.videoWidth / video.videoHeight;
    for (const s of video.querySelectorAll('source')) {
        const w = Number(s.dataset.width);
        const h = Number(s.dataset.height);
        if (w > 0 && h > 0)
            return w / h;
    }
    return 0;
}
/** How much of the needed width a rendition must actually carry before it counts as covering the box. */
const MIN_COVERAGE = 0.8;
/**
 * The rendition to actually download: the smallest one that still covers the box the clip is painted in,
 * at this screen's pixel density.
 *
 * This runs at PROMOTE time, which is the whole reason it can be exact — the element is on screen by then,
 * so `getBoundingClientRect()` is the real painted width rather than a `media` query's guess about it. A
 * band rendered 390 px wide on a phone takes the 640 file; the same band at 600 px on a 2x desktop takes
 * the 1280 one.
 *
 * Sources with no `data-width` are clips uploaded before renditions existed: there is exactly one of them,
 * and it must still play, so an unmeasurable set falls through to "the first one".
 */
/**
 * The subset of [sources] this browser can actually decode.
 *
 * Falls back to the WHOLE list when the filter would empty it. That is not defensive noise: `canPlayType`
 * answers `''` for everything in a few real places (a jsdom test, an exotic embedded webview), and a band
 * that plays the wrong-sized file beats a band that plays nothing. The step-down recovery still catches a
 * genuinely undecodable pick.
 */
function playableSources(sources) {
    if (sources.length < 2)
        return sources;
    const kept = sources.filter((s) => canPlay(s.getAttribute('type')));
    return kept.length ? kept : sources;
}
/**
 * Whether this browser decodes a source's declared type.
 *
 * `video/webm` is asked with its CODECS spelled out. The bare string is too generous: a browser that
 * carries the WebM container but not the VP9 decoder answers `maybe` to `video/webm` and then fails on
 * the file — which, because we promote exactly one source, is an empty band rather than a fallback. An
 * unknown or absent type is treated as playable, so a clip from before the ladder carried types keeps
 * working exactly as it did.
 */
function canPlay(type) {
    if (!type)
        return true;
    const probe = type === 'video/webm' ? 'video/webm; codecs="vp9,opus"' : type;
    try {
        // Deliberately NOT memoised. A module-level cache would be correct in a browser — codec support does
        // not change mid-visit — but it is global mutable state that outlives the element it was computed
        // for, and the first answer a page happens to get would then decide every later clip's ladder. The
        // call itself is a string lookup; a page full of clips makes a few dozen of them.
        return document.createElement('video').canPlayType(probe) !== '';
    }
    catch {
        // No `canPlayType` at all (a very old engine, a stubbed DOM): assume it plays and let the
        // step-down recovery deal with it if it does not.
        return true;
    }
}
function pickRendition(video, sources) {
    if (sources.length < 2)
        return sources[0];
    const measured = sources
        .map((s) => ({ s, w: Number(s.dataset.width) }))
        .filter((c) => Number.isFinite(c.w) && c.w > 0)
        .sort((a, b) => a.w - b.w);
    if (!measured.length)
        return sources[0];
    // A zero width means the element is not laid out (display:none, a collapsed tab). Don't guess small —
    // the visitor may be about to reveal it full-bleed; take the largest, which is the old behaviour.
    //
    // THE SAME MARGIN AS THE CHECK (21/09/2026). A rung is taken when it covers at least `MIN_COVERAGE` of
    // the box's device pixels — the margin `verifyRendition` already treats as invisible once the file has
    // arrived. Picking on exact coverage sent a retina laptop whose gallery needs 1 320 px past the 1 280
    // copy straight to the top rung, which on an « Original » upload is the seller's own 30 MB file: the
    // founder's product clip took a minute to start on her own screen for a 3 % shortfall no eye can see.
    // A 1.25× upscale of a moving picture is not something a visitor notices; a minute of poster is.
    const needed = neededWidth(video);
    return (measured.find((c) => c.w >= needed * MIN_COVERAGE) ?? measured[measured.length - 1]).s;
}
/**
 * The `<video>` a container drives. TWO shapes are supported and BOTH must be resolved the same way
 * everywhere in this file:
 *   · slide     — a chrome wrapper around the element: `<div data-aqx-video><video data-aqx-video-el>`.
 *   · self      — the element IS the container: `<video data-aqx-video data-aqx-video-el>` (an inline
 *                 section video has no play/sound chrome, so it would need a purely decorative wrapper
 *                 div just to satisfy a descendant selector — a DOM change that risks the section's grid).
 *
 * This helper exists because `initVideo` handled both while the autoplay orchestrator below used a bare
 * `querySelector` — which finds DESCENDANTS ONLY. Every self-container section (image-with-text, multirow,
 * results, icons-with-content) therefore resolved to `null`, was never promoted, and stayed frozen on its
 * poster for good: the seller uploaded a video and got a still image, on the storefront too.
 */
function videoOf(container) {
    return container.matches('[data-aqx-video-el]')
        ? container
        : container.querySelector('[data-aqx-video-el]');
}
function prefersReducedMotion() {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
/** `data-aqx-video-bound` — set on a container while a binding drives it (see `initVideo`). */
const BOUND = 'aqxVideoBound';
/** Wire ONE video slide's buttons + click-to-play. Autoplay is orchestrated across slides by initVideos. */
export function initVideo(container) {
    // Slide-with-chrome or self-container (see `videoOf`). For the self-container shape the optional control
    // queries below simply find nothing, and `on(null, …)` is already a no-op.
    const video = videoOf(container);
    if (!video)
        return () => { };
    // ONE binding per container (21/09/2026). The product gallery is the first clip host that is BOTH
    // upgraded by the storefront's global init (SSR markup) and hydrated as a React island that re-binds
    // whenever it mounts a new `<video>` (the desktop frame switching to the clip, the lightbox). Without
    // this mark the island bound the SSR'd players a second time: two observers, two `play()`s, and a
    // teardown that paused a clip another binding was still driving. A container that is already bound is
    // left to its binding; the mark goes away with it, so a genuine re-bind after cleanup still works.
    if (container.dataset[BOUND] === '1')
        return () => { };
    container.dataset[BOUND] = '1';
    const clickMode = container.getAttribute('data-mode') === 'click';
    const cleanups = [() => delete container.dataset[BOUND]];
    const on = (el, ev, fn, opts) => {
        if (!el)
            return;
        el.addEventListener(ev, fn, opts);
        cleanups.push(() => el.removeEventListener(ev, fn, opts));
    };
    const play = () => {
        promoteVideo(video);
        void video.play().catch(() => { });
    };
    // Play / pause toggle.
    const playBtn = container.querySelector('[data-aqx-video-play]');
    const syncPlayUi = () => {
        // On the CONTAINER, not the button: a section that autoplays wants to fade its own chrome out while
        // the clip runs, and CSS can only reach the button from an ancestor that carries the state.
        container.setAttribute('data-playing', String(!video.paused));
        if (!playBtn)
            return;
        // THE ICON IS NOT REWRITTEN HERE ANY MORE (18/09/2026). This used to be
        // `playBtn.textContent = video.paused ? '▶' : '❚❚'`, and `textContent` erases the button's children —
        // which is precisely why the chrome could never be anything but a text glyph painted by whatever font
        // the VISITOR happens to have. The themed chrome (`_shared/videoControls.tsx`) ships BOTH icons as
        // SVG and lets the `data-playing` attribute above decide which one is visible, so a toggle now costs
        // one attribute and touches no DOM the section owns.
        // The legacy glyph path stays for markup that has no icons (a seller's own template, an older cached
        // page): it only fires when the button really is a bare text node.
        if (!playBtn.querySelector('svg'))
            playBtn.textContent = video.paused ? '▶' : '❚❚';
        // The two sentences travel in the MARKUP, in the reader's language — `videoInit` is the React-free
        // engine shipped to every storefront and has no dictionary. Absent (legacy markup) → keep whatever
        // label the section wrote rather than stamping an English one over a Polish page.
        const label = video.paused ? playBtn.dataset.labelPlay : playBtn.dataset.labelPause;
        if (label)
            playBtn.setAttribute('aria-label', label);
    };
    on(playBtn, 'click', () => {
        if (video.paused) {
            // A visitor who hits play behind the observer's back is asking for it: clear the veto.
            delete container.dataset.userPaused;
            play();
        }
        else {
            // Remember the VETO. Without it the next intersection callback (any scroll) restarts the clip and
            // the pause button looks broken — it is the first thing anyone tries on an autoplaying tile.
            container.dataset.userPaused = '1';
            video.pause();
        }
    });
    on(video, 'play', syncPlayUi);
    on(video, 'pause', syncPlayUi);
    // Click-to-play: clicking anywhere on the poster (no interactive link on this slide) plays with sound.
    if (clickMode)
        on(container, 'click', () => video.paused && play());
    // Mute toggle.
    const soundBtn = container.querySelector('[data-aqx-video-sound]');
    const syncSoundUi = () => {
        // Same split as the play disc: the state goes on the container (CSS picks the icon), the glyph
        // rewrite only survives for icon-less legacy markup.
        container.setAttribute('data-muted', String(video.muted));
        if (!soundBtn)
            return;
        if (!soundBtn.querySelector('svg'))
            soundBtn.textContent = video.muted ? '🔇' : '🔊';
        const label = video.muted ? soundBtn.dataset.labelUnmute : soundBtn.dataset.labelMute;
        if (label)
            soundBtn.setAttribute('aria-label', label);
        soundBtn.setAttribute('aria-pressed', String(!video.muted));
    };
    on(soundBtn, 'click', (e) => {
        e.stopPropagation();
        video.muted = !video.muted;
        syncSoundUi();
    });
    // Fullscreen.
    const fsBtn = container.querySelector('[data-aqx-video-fs]');
    on(fsBtn, 'click', (e) => {
        e.stopPropagation();
        void video.requestFullscreen?.().catch(() => { });
    });
    // Timeline: reflect progress + click-to-seek.
    const timeline = container.querySelector('[data-aqx-video-timeline]');
    const progress = container.querySelector('[data-aqx-video-progress]');
    on(video, 'timeupdate', () => {
        if (progress && video.duration > 0)
            progress.style.inlineSize = `${(video.currentTime / video.duration) * 100}%`;
    });
    on(timeline, 'click', (e) => {
        if (!timeline || !(video.duration > 0))
            return;
        const r = timeline.getBoundingClientRect();
        const ratio = (e.clientX - r.left) / r.width || 0;
        video.currentTime = Math.max(0, Math.min(1, ratio)) * video.duration;
    });
    syncPlayUi();
    syncSoundUi();
    return () => cleanups.forEach((c) => c());
}
/** A container is "on screen enough" to deserve playing. */
const VISIBLE = 0.25;
/**
 * How wide a band must be, as a share of the viewport, to earn its download A SCREEN EARLY.
 *
 * THIS IS THE 2026-09-18 REPORT ("le composant vidéo prend énormément de temps pour se lire, les autres
 * sont parfaites"). Nothing was broken in the engine: the Video section is the only band painted at full
 * page width, and the picker serves the rung that covers the box it is actually painted in. Measured on
 * her own product page in real Chromium at 1512 px, every clip on the page, same engine, same server:
 *
 * | section              | boîte peinte | fichier servi | octets    | 1re image |
 * |---|---|---|---|---|
 * | **Vidéo (pleine largeur)** | 1400 x 933 | 1248 x 1664 | **2 359 459** | **6,9 s**, ré-tamponne jusqu'à 16 s |
 * | image + texte        | 640 x 640    | 640 x 640     | 274 102   | ~1,1 s |
 * | multicolonnes        | 440 x 587    | 480 x 640     | ~181 000  | ~1,5 s |
 *
 * The full-bleed band has 8.6x more bytes to pull than its neighbours and — with `preload="none"` and a
 * 200 px margin — starts pulling them from zero at the moment the visitor is already looking at it. A card
 * gets away with that because 274 kB lands in a blink; a 2.4 MB band cannot. So the wide band, and only
 * the wide band, starts a whole screen ahead: the bytes are in flight while the visitor is still reading
 * the section above, and the rung it picks is unchanged (the box is laid out whether or not it is on
 * screen, so `pickRendition` measures exactly the same thing).
 *
 * On a phone every band is full width, so they all qualify — and there they all take the 640 rung, which
 * is the cheap one. The cost of the head start is paid where it is smallest and bought where it hurts.
 */
const FULL_BLEED_SHARE = 0.75;
/**
 * Initialise every video under `root` and drive muted autoplay from an Intersection Observer: a clip loads
 * only once it nears the viewport (`preload="none"` + `data-src` until then) and pauses when it leaves.
 * Under `prefers-reduced-motion` NOTHING autoplays — the poster stays, by design.
 *
 * TWO autoplay policies, keyed on the container shape (see `videoOf`):
 *   · SLIDES (`<div data-aqx-video><video>`) — sliders and heroes stack several slides in the SAME viewport
 *     slot, and an inactive slide can still be "intersecting" (opacity/transform are invisible to an IO).
 *     They keep the exclusive budget: only the single most-visible slide plays, so a 5-slide hero never
 *     downloads 5 clips at once.
 *   · INLINE SECTION MEDIA (`<video data-aqx-video data-aqx-video-el>`) — image-with-text, multirow rows,
 *     results, icons-with-content. Each is a distinct band the visitor scrolls through, and the founder's
 *     contract is that such a video renders EXACTLY like an image (silent loop, no chrome). Making them
 *     compete for one slot means the moment two bands share the screen one of them freezes on its poster —
 *     i.e. it looks like a still image, which is the bug this policy split fixes. They play whenever they
 *     are visible. The deferred download (the actual bandwidth win) is untouched: still nothing until in view.
 *
 * Returns a cleanup so a LIVE React host (the builder canvas) can re-bind when the media changes without
 * leaking listeners; the storefront's static global init ignores the return (pages are static).
 */
/**
 * Appelle `fn` une seule fois, au premier moment creux APRÈS la première peinture — et tout de suite
 * si la page a déjà fini de charger.
 *
 * Pourquoi (mesuré au Lighthouse mobile sur mooveshift, 17/10/2026). La 1re diapo de sa galerie est un
 * clip en lecture automatique. Son AFFICHE est l'élément LCP, elle pèse 20 Kio et elle est préchargée
 * en priorité haute — et elle mettait quand même 2,0 s à descendre. La raison n'est pas sa taille :
 * c'est que l'observateur armait `preload="auto"` + `play()` dans le même temps, et que le clip
 * (287 Kio) partait en parallèle sur une ligne qui en porte 200 par seconde. L'acheteuse attendait
 * l'image fixe derrière la vidéo censée la remplacer.
 *
 * Le report ne coûte rien de visible : jusqu'à ce que le clip ait assez d'octets pour bouger,
 * l'écran montre l'affiche — c'est-à-dire exactement ce qu'il montre pendant l'attente de toute façon.
 *
 * `readyState === 'complete'` est le cas du canevas de l'éditeur de thèmes et de toute reliaison
 * React après hydratation : là il n'y a plus de première peinture à protéger, et attendre une seconde
 * ferait clignoter le travail de la vendeuse pour rien.
 */
function afterFirstPaint(fn) {
    if (typeof document === 'undefined' || document.readyState === 'complete') {
        fn();
        return () => { };
    }
    let done = false;
    const once = () => {
        if (done)
            return;
        done = true;
        fn();
    };
    // Échéance ferme : un onglet en arrière-plan ne peint jamais, donc `requestAnimationFrame` n'y tourne
    // pas. Sans ce filet, un clip ne démarrerait jamais dans un onglet ouvert en seconde main.
    const timer = setTimeout(once, FIRST_PAINT_GRACE_MS);
    const idle = () => {
        const ric = globalThis
            .requestIdleCallback;
        if (typeof ric === 'function')
            ric(once, { timeout: FIRST_PAINT_GRACE_MS });
        else
            once();
    };
    if (typeof requestAnimationFrame === 'function')
        requestAnimationFrame(() => requestAnimationFrame(idle));
    else
        idle();
    return () => {
        done = true;
        clearTimeout(timer);
    };
}
/** Le délai au bout duquel on démarre les clips quoi qu'il arrive — au-delà, l'attente se verrait. */
const FIRST_PAINT_GRACE_MS = 1200;
export function initVideos(root) {
    // Only the containers nobody drives yet — see the one-binding rule in `initVideo`. The orchestration
    // below is scoped the same way: a clip another binding already plays must not be paused by this one.
    const containers = Array.from(root.querySelectorAll('[data-aqx-video]')).filter((c) => c.dataset[BOUND] !== '1');
    const cleanups = containers.map((c) => initVideo(c));
    const teardown = () => cleanups.forEach((c) => c());
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined')
        return teardown;
    const autoplay = containers.filter((c) => c.getAttribute('data-mode') === 'autoplay');
    if (!autoplay.length)
        return teardown;
    // Inline = "plays on its own merit". Two shapes qualify: the element IS the container (image-with-text,
    // multirow, results, icons-with-content), or a wrapper says so explicitly with `data-aqx-video-inline`
    // (the collage tile, which needs a wrapper for its play button but is still a band, not a slide).
    const isInline = (c) => c.matches('[data-aqx-video-el]') || c.hasAttribute('data-aqx-video-inline');
    const slides = autoplay.filter((c) => !isInline(c));
    const inline = autoplay
        .filter(isInline)
        .map((box) => ({ box, video: videoOf(box) }))
        .filter((p) => p.video !== null);
    // THE HEAD START — a SECOND observer, and it has to be a second one. Widening the margin of the player
    // below would make a band that is still a screen away report `intersectionRatio: 1` against the expanded
    // root, and it would start PLAYING off screen. Downloading early and playing on time are two different
    // questions, so they get two different observers.
    //
    // `preload` is what actually moves the bytes: the markup ships `preload="none"`, and with that a `load()`
    // fetches NOTHING — the autoplay path only works because `play()` forces it. Promoting early without
    // lifting `preload` would buy exactly zero.
    const preloadIo = new IntersectionObserver((entries) => {
        for (const e of entries) {
            if (!e.isIntersecting)
                continue;
            const el = e.target;
            // The IO hands us the measured box, so the "is this a band or a card" question costs no layout.
            // An entry without one (a partial polyfill, a test double) falls back to asking the element —
            // never to "assume it is a band", because the wrong answer here spends a visitor's data.
            const width = e.boundingClientRect?.width ?? el.getBoundingClientRect().width;
            if (width < window.innerWidth * FULL_BLEED_SHARE)
                continue;
            const v = videoOf(el);
            if (!v)
                continue;
            // A visitor who pressed pause is not waiting on anything — don't spend their data.
            if (el.dataset.userPaused === '1')
                continue;
            preloadIo.unobserve(el);
            v.preload = 'auto';
            promoteVideo(v);
        }
    }, 
    // One screen of runway. `rootMargin` is fixed at construction, which is what we want: a viewport that
    // changes mid-scroll (an address bar collapsing) must not reshuffle what is already downloading.
    { rootMargin: `${Math.round(window.innerHeight)}px 0px`, threshold: 0 });
    // Armé après la première peinture : voir `afterFirstPaint`. Le clip d'une 1re diapo volait la bande
    // passante de sa propre affiche, qui est l'élément LCP de la page produit.
    let cancelPreloadGate = afterFirstPaint(() => inline.forEach(({ box }) => preloadIo.observe(box)));
    const ratios = new Map();
    let current = null;
    const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => ratios.set(e.target, e.isIntersecting ? e.intersectionRatio : 0));
        // Inline section media: each one plays on its own merit, none blocks another.
        for (const { box, video: v } of inline) {
            if ((ratios.get(box) ?? 0) > VISIBLE) {
                // A visitor who pressed pause keeps it paused, however much they scroll.
                if (box.dataset.userPaused === '1')
                    continue;
                promoteVideo(v);
                void v.play().catch(() => { });
            }
            else if (!v.paused) {
                v.pause();
            }
        }
        // Slides: the single most-visible one wins. A for-of loop (not forEach) so TS tracks the assignment
        // to `best` across the closure boundary.
        let best = null;
        let bestRatio = VISIBLE;
        for (const c of slides) {
            const r = ratios.get(c) ?? 0;
            if (r > bestRatio) {
                bestRatio = r;
                best = c;
            }
        }
        const nextVideo = best ? videoOf(best) : null;
        if (nextVideo === current)
            return;
        current?.pause(); // pause the one leaving the viewport (bandwidth + the single-autoplay budget)
        current = nextVideo;
        if (current) {
            promoteVideo(current);
            void current.play().catch(() => { });
        }
    }, { rootMargin: '200px 0px', threshold: [0, 0.25, 0.5, 0.75, 1] });
    let cancelPlayGate = afterFirstPaint(() => autoplay.forEach((c) => io.observe(c)));
    return () => {
        cancelPreloadGate();
        cancelPlayGate();
        io.disconnect();
        preloadIo.disconnect();
        current?.pause();
        // Inline media plays outside the single-slot budget, so `current` doesn't cover it: stop each one or a
        // builder re-bind leaves the previous clip decoding in a detached node.
        inline.forEach(({ video: v }) => !v.paused && v.pause());
        teardown();
    };
}
//# sourceMappingURL=videoInit.js.map